import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, MinusCircle, FileText } from 'lucide-react';

const AccessibilityConformanceReport = () => {
  const evaluationDate = "February 27, 2026";
  const reportVersion = "1.0";
  
  // Conformance status definitions
  const STATUS = {
    SUPPORTS: { label: "Supports", color: "text-green-700", bg: "bg-green-50", icon: CheckCircle },
    PARTIALLY: { label: "Partially Supports", color: "text-yellow-700", bg: "bg-yellow-50", icon: AlertCircle },
    NOT_SUPPORT: { label: "Does Not Support", color: "text-red-700", bg: "bg-red-50", icon: AlertCircle },
    NA: { label: "Not Applicable", color: "text-gray-600", bg: "bg-gray-50", icon: MinusCircle }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon size={12} className="mr-1" aria-hidden="true" />
        {config.label}
      </span>
    );
  };

  const wcagCriteria = [
    // Principle 1: Perceivable
    { 
      id: "1.1.1", 
      name: "Non-text Content", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "All images include meaningful alternative text. Decorative icons use aria-hidden=\"true\" to hide from assistive technologies."
    },
    { 
      id: "1.2.1", 
      name: "Audio-only and Video-only (Prerecorded)", 
      level: "A", 
      status: "NA",
      remarks: "No prerecorded audio-only or video-only content on site."
    },
    { 
      id: "1.2.2", 
      name: "Captions (Prerecorded)", 
      level: "A", 
      status: "NA",
      remarks: "No prerecorded synchronized media on site."
    },
    { 
      id: "1.2.3", 
      name: "Audio Description or Media Alternative (Prerecorded)", 
      level: "A", 
      status: "NA",
      remarks: "No prerecorded synchronized media on site."
    },
    { 
      id: "1.2.4", 
      name: "Captions (Live)", 
      level: "AA", 
      status: "NA",
      remarks: "No live audio content on site."
    },
    { 
      id: "1.2.5", 
      name: "Audio Description (Prerecorded)", 
      level: "AA", 
      status: "NA",
      remarks: "No prerecorded video content on site."
    },
    { 
      id: "1.3.1", 
      name: "Info and Relationships", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Semantic HTML structure used throughout. Proper heading hierarchy (H1-H4), form labels associated with inputs, ARIA landmarks defined (header, nav, main, footer)."
    },
    { 
      id: "1.3.2", 
      name: "Meaningful Sequence", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Content reading order matches visual presentation. DOM order is logical and sequential."
    },
    { 
      id: "1.3.3", 
      name: "Sensory Characteristics", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Instructions do not rely solely on shape, color, size, visual location, orientation, or sound."
    },
    { 
      id: "1.3.4", 
      name: "Orientation", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Content displays correctly in both portrait and landscape orientations. No orientation lock implemented."
    },
    { 
      id: "1.3.5", 
      name: "Identify Input Purpose", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Form inputs include appropriate autocomplete attributes (name, organization, email, tel) enabling user agent assistance."
    },
    { 
      id: "1.4.1", 
      name: "Use of Color", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Color is not used as the only visual means of conveying information. Error states include text messages and icons in addition to color changes."
    },
    { 
      id: "1.4.2", 
      name: "Audio Control", 
      level: "A", 
      status: "NA",
      remarks: "No audio content that plays automatically for more than 3 seconds."
    },
    { 
      id: "1.4.3", 
      name: "Contrast (Minimum)", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Text contrast ratios verified: Headings (#1e3a5f on light backgrounds) exceed 9:1. Body text (#374151, #4b5563) exceeds 6:1. All text meets or exceeds 4.5:1 minimum."
    },
    { 
      id: "1.4.4", 
      name: "Resize Text", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Text can be resized up to 200% without loss of content or functionality. Site uses relative units (rem) for typography."
    },
    { 
      id: "1.4.5", 
      name: "Images of Text", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "No images of text used except for the company logo, which is essential for branding."
    },
    { 
      id: "1.4.10", 
      name: "Reflow", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Content reflows without horizontal scrolling at 320px viewport width. Responsive design implemented with Tailwind CSS breakpoints."
    },
    { 
      id: "1.4.11", 
      name: "Non-text Contrast", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "User interface components (buttons, form inputs, icons) have at least 3:1 contrast ratio against adjacent colors."
    },
    { 
      id: "1.4.12", 
      name: "Text Spacing", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "No loss of content when text spacing is adjusted. Site uses relative units allowing user customization."
    },
    { 
      id: "1.4.13", 
      name: "Content on Hover or Focus", 
      level: "AA", 
      status: "NA",
      remarks: "No additional content appears on hover or focus that would require dismissibility, persistence, or hovering."
    },
    // Principle 2: Operable
    { 
      id: "2.1.1", 
      name: "Keyboard", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "All functionality available via keyboard. Interactive elements (links, buttons, form controls) are focusable and operable."
    },
    { 
      id: "2.1.2", 
      name: "No Keyboard Trap", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Keyboard focus can be moved away from all components using standard navigation (Tab, Shift+Tab). No keyboard traps identified."
    },
    { 
      id: "2.1.4", 
      name: "Character Key Shortcuts", 
      level: "A", 
      status: "NA",
      remarks: "No single-character key shortcuts implemented."
    },
    { 
      id: "2.2.1", 
      name: "Timing Adjustable", 
      level: "A", 
      status: "NA",
      remarks: "No time limits on content interaction."
    },
    { 
      id: "2.2.2", 
      name: "Pause, Stop, Hide", 
      level: "A", 
      status: "NA",
      remarks: "No auto-updating, moving, blinking, or scrolling content that starts automatically."
    },
    { 
      id: "2.3.1", 
      name: "Three Flashes or Below Threshold", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "No content flashes more than three times per second."
    },
    { 
      id: "2.4.1", 
      name: "Bypass Blocks", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Skip navigation link provided: 'Skip to main content' link appears on keyboard focus and targets main content area."
    },
    { 
      id: "2.4.2", 
      name: "Page Titled", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "All pages have descriptive titles that identify the page purpose within the site context."
    },
    { 
      id: "2.4.3", 
      name: "Focus Order", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Focus order follows logical reading sequence. Tab navigation proceeds in meaningful order through page content."
    },
    { 
      id: "2.4.4", 
      name: "Link Purpose (In Context)", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Link purposes are clear from link text or surrounding context. 'Learn more' links include aria-label attributes providing full context."
    },
    { 
      id: "2.4.5", 
      name: "Multiple Ways", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Multiple navigation methods provided: primary navigation menu, footer links, in-page links, and direct URL access."
    },
    { 
      id: "2.4.6", 
      name: "Headings and Labels", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Headings and form labels are descriptive and clearly identify their associated content or purpose."
    },
    { 
      id: "2.4.7", 
      name: "Focus Visible", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Visible focus indicators provided for all interactive elements. Focus rings and outline styles clearly indicate current focus position."
    },
    { 
      id: "2.4.11", 
      name: "Focus Not Obscured (Minimum)", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "WCAG 2.2: Focused elements are not entirely hidden by sticky header. Adequate spacing ensures focus visibility."
    },
    { 
      id: "2.5.1", 
      name: "Pointer Gestures", 
      level: "A", 
      status: "NA",
      remarks: "No functionality requires multipoint or path-based gestures."
    },
    { 
      id: "2.5.2", 
      name: "Pointer Cancellation", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Standard click/tap events used. Actions triggered on up-event, allowing cancellation."
    },
    { 
      id: "2.5.3", 
      name: "Label in Name", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Accessible names of UI components contain the visible text labels."
    },
    { 
      id: "2.5.4", 
      name: "Motion Actuation", 
      level: "A", 
      status: "NA",
      remarks: "No functionality operated by device motion or user motion."
    },
    { 
      id: "2.5.7", 
      name: "Dragging Movements", 
      level: "AA", 
      status: "NA",
      remarks: "WCAG 2.2: No functionality requires dragging movements."
    },
    { 
      id: "2.5.8", 
      name: "Target Size (Minimum)", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "WCAG 2.2: Interactive targets meet 24x24 CSS pixel minimum. Navigation links have 44px minimum height for enhanced accessibility."
    },
    // Principle 3: Understandable
    { 
      id: "3.1.1", 
      name: "Language of Page", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Page language declared: <html lang=\"en\"> attribute present on all pages."
    },
    { 
      id: "3.1.2", 
      name: "Language of Parts", 
      level: "AA", 
      status: "NA",
      remarks: "All content is in English. No passages in other languages."
    },
    { 
      id: "3.2.1", 
      name: "On Focus", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Receiving focus does not initiate unexpected changes of context."
    },
    { 
      id: "3.2.2", 
      name: "On Input", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Changing input values does not automatically cause unexpected context changes. Form submission requires explicit button activation."
    },
    { 
      id: "3.2.3", 
      name: "Consistent Navigation", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Navigation menus appear in consistent locations and order across all pages."
    },
    { 
      id: "3.2.4", 
      name: "Consistent Identification", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Components with same functionality are identified consistently throughout the site."
    },
    { 
      id: "3.2.6", 
      name: "Consistent Help", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "WCAG 2.2: Help mechanisms (Contact link, phone number) appear in consistent locations across all pages."
    },
    { 
      id: "3.3.1", 
      name: "Error Identification", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Form validation errors are identified in text and announced to screen readers via role=\"alert\". Error messages appear adjacent to the relevant field."
    },
    { 
      id: "3.3.2", 
      name: "Labels or Instructions", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Form inputs have associated labels. Required fields marked with asterisk (*) and aria-required attribute."
    },
    { 
      id: "3.3.3", 
      name: "Error Suggestion", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Error messages provide specific guidance on how to correct input errors (e.g., 'Please enter your name')."
    },
    { 
      id: "3.3.4", 
      name: "Error Prevention (Legal, Financial, Data)", 
      level: "AA", 
      status: "NA",
      remarks: "No legal commitments, financial transactions, or user-controllable data modification."
    },
    { 
      id: "3.3.7", 
      name: "Redundant Entry", 
      level: "A", 
      status: "NA",
      remarks: "WCAG 2.2: No multi-step processes requiring re-entry of previously provided information."
    },
    { 
      id: "3.3.8", 
      name: "Accessible Authentication (Minimum)", 
      level: "AA", 
      status: "NA",
      remarks: "WCAG 2.2: No authentication functionality on site."
    },
    // Principle 4: Robust
    { 
      id: "4.1.1", 
      name: "Parsing", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "Note: Obsolete in WCAG 2.2, retained for backward compatibility. HTML is well-formed with proper nesting and unique IDs."
    },
    { 
      id: "4.1.2", 
      name: "Name, Role, Value", 
      level: "A", 
      status: "SUPPORTS",
      remarks: "User interface components have accessible names and roles. ARIA attributes correctly applied to custom components."
    },
    { 
      id: "4.1.3", 
      name: "Status Messages", 
      level: "AA", 
      status: "SUPPORTS",
      remarks: "Status messages (form success/error) use role=\"status\" with aria-live=\"polite\" for screen reader announcement without receiving focus."
    }
  ];

  const groupedCriteria = {
    perceivable: wcagCriteria.filter(c => c.id.startsWith('1.')),
    operable: wcagCriteria.filter(c => c.id.startsWith('2.')),
    understandable: wcagCriteria.filter(c => c.id.startsWith('3.')),
    robust: wcagCriteria.filter(c => c.id.startsWith('4.'))
  };

  const stats = {
    supports: wcagCriteria.filter(c => c.status === 'SUPPORTS').length,
    partially: wcagCriteria.filter(c => c.status === 'PARTIALLY').length,
    notSupport: wcagCriteria.filter(c => c.status === 'NOT_SUPPORT').length,
    na: wcagCriteria.filter(c => c.status === 'NA').length
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <FileText size={32} className="mr-3" aria-hidden="true" />
            <h1 className="text-3xl font-bold">Accessibility Conformance Report</h1>
          </div>
          <p className="text-gray-300 text-lg">WCAG 2.2 Level AA Evaluation</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Report Information */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Report Information</h2>
          <div className="bg-stone-50 rounded-lg p-6 border border-gray-200">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Product Name</dt>
                <dd className="text-gray-900">Lone Star Republic Consulting Website</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Product URL</dt>
                <dd className="text-gray-900">
                  <a href="https://repo-to-web-7.preview.emergentagent.com" className="text-[#c84630] hover:underline">
                    west-texas-pro.preview.emergentagent.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Report Date</dt>
                <dd className="text-gray-900">{evaluationDate}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Report Version</dt>
                <dd className="text-gray-900">{reportVersion}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Evaluation Standard</dt>
                <dd className="text-gray-900">WCAG 2.2 Level AA</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Applicable Standards</dt>
                <dd className="text-gray-900">Texas TAC §206/§213, Section 508</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Executive Summary */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Executive Summary</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              This Accessibility Conformance Report (ACR) documents the evaluation of the Lone Star Republic Consulting 
              website against the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA success criteria. The evaluation 
              was conducted to assess conformance with accessibility standards applicable to vendors serving Texas public entities.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Based on this evaluation, the website <strong>supports</strong> the WCAG 2.2 Level AA requirements. 
              The site has been designed and developed with accessibility as a priority, incorporating semantic HTML, 
              ARIA landmarks, keyboard navigation, and screen reader compatibility.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              The website demonstrates strong accessibility performance across applicable WCAG 2.2 Level AA success criteria. 
              Criteria related to prerecorded media, live media, and other non-applicable content types are marked 
              <strong> Not Applicable</strong> where appropriate.
            </p>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
              <div className="text-3xl font-bold text-green-700">{stats.supports}</div>
              <div className="text-sm text-green-600">Supports</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200">
              <div className="text-3xl font-bold text-yellow-700">{stats.partially}</div>
              <div className="text-sm text-yellow-600">Partially Supports</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
              <div className="text-3xl font-bold text-red-700">{stats.notSupport}</div>
              <div className="text-sm text-red-600">Does Not Support</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
              <div className="text-3xl font-bold text-gray-700">{stats.na}</div>
              <div className="text-sm text-gray-600">Not Applicable</div>
            </div>
          </div>
        </section>

        {/* Conformance Level */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Conformance Level</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center">
              <CheckCircle size={24} className="text-green-600 mr-3" aria-hidden="true" />
              <div>
                <p className="font-semibold text-green-800">WCAG 2.2 Level AA: Supports</p>
                <p className="text-sm text-green-700 mt-1">
                  The website supports the evaluated standard. Applicable success criteria are met or exceed requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Terms Used */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Terms Used in This Report</h2>
          <div className="space-y-3">
            <div className="flex items-start">
              <StatusBadge status="SUPPORTS" />
              <p className="ml-3 text-gray-700 text-sm">The functionality of the product has at least one method that meets the criterion without known defects or meets with equivalent facilitation.</p>
            </div>
            <div className="flex items-start">
              <StatusBadge status="PARTIALLY" />
              <p className="ml-3 text-gray-700 text-sm">Some functionality of the product does not meet the criterion.</p>
            </div>
            <div className="flex items-start">
              <StatusBadge status="NOT_SUPPORT" />
              <p className="ml-3 text-gray-700 text-sm">The majority of product functionality does not meet the criterion.</p>
            </div>
            <div className="flex items-start">
              <StatusBadge status="NA" />
              <p className="ml-3 text-gray-700 text-sm">The criterion is not relevant to the product.</p>
            </div>
          </div>
        </section>

        {/* Detailed Evaluation */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">WCAG 2.2 Level AA Evaluation Results</h2>
          
          {/* Principle 1: Perceivable */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4 pb-2 border-b border-gray-200">
              Principle 1: Perceivable
            </h3>
            <p className="text-gray-600 text-sm mb-4">Information and user interface components must be presentable to users in ways they can perceive.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Criterion</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Level</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedCriteria.perceivable.map((criterion) => (
                    <tr key={criterion.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{criterion.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{criterion.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{criterion.level}</td>
                      <td className="px-4 py-3"><StatusBadge status={criterion.status} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{criterion.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Principle 2: Operable */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4 pb-2 border-b border-gray-200">
              Principle 2: Operable
            </h3>
            <p className="text-gray-600 text-sm mb-4">User interface components and navigation must be operable.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Criterion</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Level</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedCriteria.operable.map((criterion) => (
                    <tr key={criterion.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{criterion.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{criterion.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{criterion.level}</td>
                      <td className="px-4 py-3"><StatusBadge status={criterion.status} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{criterion.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Principle 3: Understandable */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4 pb-2 border-b border-gray-200">
              Principle 3: Understandable
            </h3>
            <p className="text-gray-600 text-sm mb-4">Information and the operation of user interface must be understandable.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Criterion</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Level</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedCriteria.understandable.map((criterion) => (
                    <tr key={criterion.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{criterion.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{criterion.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{criterion.level}</td>
                      <td className="px-4 py-3"><StatusBadge status={criterion.status} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{criterion.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Principle 4: Robust */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4 pb-2 border-b border-gray-200">
              Principle 4: Robust
            </h3>
            <p className="text-gray-600 text-sm mb-4">Content must be robust enough that it can be interpreted reliably by a wide variety of user agents, including assistive technologies.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Criterion</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Level</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedCriteria.robust.map((criterion) => (
                    <tr key={criterion.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{criterion.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{criterion.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{criterion.level}</td>
                      <td className="px-4 py-3"><StatusBadge status={criterion.status} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{criterion.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Known Limitations */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Known Limitations</h2>
          <div className="bg-stone-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-700">
              No known accessibility limitations have been identified at this time. The website has been 
              tested with keyboard navigation, screen reader software, and automated accessibility testing tools.
            </p>
          </div>
        </section>

        {/* Testing Methods */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Evaluation Methods</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Automated Testing</h3>
              <p className="text-gray-700 text-sm">
                Automated accessibility scanning using industry-standard tools to identify potential WCAG violations.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Manual Testing</h3>
              <p className="text-gray-700 text-sm">
                Keyboard navigation testing, focus order verification, and semantic structure analysis.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Assistive Technology Testing</h3>
              <p className="text-gray-700 text-sm">
                Screen reader compatibility testing and ARIA attribute verification.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Responsive Testing</h3>
              <p className="text-gray-700 text-sm">
                Testing at multiple viewport sizes including 320px width for reflow compliance.
              </p>
            </div>
          </div>
        </section>

        {/* Ongoing Monitoring */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Ongoing Monitoring Statement</h2>
          <div className="bg-stone-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-700 leading-relaxed">
              Lone Star Republic Consulting is committed to maintaining accessibility conformance. 
              We conduct periodic reviews to ensure continued compliance with WCAG 2.2 Level AA standards. 
              Any accessibility concerns can be reported to{' '}
              <a href="mailto:hello@lonestarrepublicco.com" className="text-[#c84630] hover:underline">
                hello@lonestarrepublicco.com
              </a>
              {' '}and will be addressed promptly.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Feedback and Contact</h2>
          <div className="bg-stone-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-700 mb-4">
              We welcome feedback on the accessibility of this website. If you encounter any accessibility 
              barriers or have suggestions for improvement, please contact us:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Email:</strong> <a href="mailto:hello@lonestarrepublicco.com" className="text-[#c84630] hover:underline">hello@lonestarrepublicco.com</a></li>
              <li><strong>Phone:</strong> <a href="tel:325-305-2777" className="text-[#c84630] hover:underline">325-305-2777</a></li>
            </ul>
          </div>
        </section>

        {/* Back Link */}
        <div className="text-center pt-8 border-t border-gray-200">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#c84630] hover:text-[#a83825] font-medium"
          >
            ← Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityConformanceReport;
